import { BalancesAndAllowances } from '@cowprotocol/balances-and-allowances'
import { isEnoughAmount } from '@cowprotocol/common-utils'
import { getAddressKey, SupportedChainId } from '@cowprotocol/cow-sdk'
import { Currency, CurrencyAmount, Percent, Token } from '@cowprotocol/currency'

import { RateInfoParams } from 'common/pure/RateInfo'
import { getOrderPermitAmount } from 'utils/orderUtils/getOrderPermitAmount'
import { ParsedOrder } from 'utils/orderUtils/parseOrder'

import { PendingOrdersPermitValidityState } from '../state/permit/pendingOrdersPermitValidity.atom'

export interface OrderParams {
  chainId: SupportedChainId | undefined
  sellAmount: CurrencyAmount<Currency>
  buyAmount: CurrencyAmount<Currency>
  rateInfoParams: RateInfoParams
  hasEnoughBalance: boolean | undefined
  hasEnoughAllowance: boolean | undefined
}

const PERCENTAGE_FOR_PARTIAL_FILLS = new Percent(5, 10000) // 0.05%

export function getOrderParams(
  chainId: SupportedChainId,
  balancesAndAllowances: BalancesAndAllowances,
  order: ParsedOrder,
  pendingOrdersPermitValidityState?: PendingOrdersPermitValidityState,
): OrderParams {
  const isOrderAtLeastOnceFilled = order.executionData.filledAmount.gt(0)
  const sellAmount = CurrencyAmount.fromRawAmount(order.inputToken, order.sellAmount)
  const buyAmount = CurrencyAmount.fromRawAmount(order.outputToken, order.buyAmount)
  const isPermitInvalid = pendingOrdersPermitValidityState
    ? pendingOrdersPermitValidityState[order.id] === false
    : false
  const shouldCheckFunding = order.isEoaTwapOrder !== true
  const permitAmount =
    shouldCheckFunding && !isPermitInvalid ? getOrderPermitAmount(chainId, order) || undefined : undefined

  const rateInfoParams: RateInfoParams = {
    chainId,
    inputCurrencyAmount: sellAmount,
    outputCurrencyAmount: buyAmount,
    activeRateFiatAmount: null,
    invertedActiveRateFiatAmount: null,
  }

  const { balances, allowances } = balancesAndAllowances
  const balance = shouldCheckFunding ? balances[getAddressKey(order.inputToken.address)] : undefined
  const allowance = shouldCheckFunding ? allowances[getAddressKey(order.inputToken.address)] : undefined

  const { hasEnoughBalance, hasEnoughAllowance } = _hasEnoughBalanceAndAllowance({
    partiallyFillable: order.partiallyFillable,
    sellAmount,
    balance,
    // If the order has been filled at least once, we should not consider the permit amount
    allowance: !isOrderAtLeastOnceFilled ? getBiggerAmount(allowance, permitAmount) : allowance,
  })

  return {
    chainId,
    sellAmount,
    buyAmount,
    rateInfoParams,
    hasEnoughBalance,
    hasEnoughAllowance,
  }
}

function _hasEnoughBalanceAndAllowance(params: {
  balance: bigint | undefined
  allowance: bigint | undefined
  partiallyFillable: boolean
  sellAmount: CurrencyAmount<Token>
}): {
  hasEnoughBalance: boolean | undefined
  hasEnoughAllowance: boolean | undefined
} {
  const { allowance, balance, partiallyFillable, sellAmount } = params
  // Check there's at least PERCENTAGE_FOR_PARTIAL_FILLS of balance/allowance to consider it as enough
  const amount = partiallyFillable ? sellAmount.multiply(PERCENTAGE_FOR_PARTIAL_FILLS) : sellAmount
  const hasEnoughBalance = isEnoughAmount(amount, balance)
  const hasEnoughAllowance = isEnoughAmount(amount, allowance)

  return { hasEnoughBalance, hasEnoughAllowance }
}

function getBiggerAmount(a: bigint | undefined, b: bigint | undefined): bigint | undefined {
  if (a === undefined) return b
  if (b === undefined) return a

  return a > b ? a : b
}
